/**
 * Single source of truth for the 5 portal origins in the multi-Amplify
 * topology + a strict allowlist validator used by every place that
 * could otherwise become an open-redirect vector.
 *
 * Origins
 * -------
 *   - `clients-tmp.portal.tryrook.io`     — landing during the legacy
 *                                            Vercel cutover (CPF-NFR-026
 *                                            transitional state)
 *   - `clients.portal.tryrook.io`         — landing post-cutover
 *   - `production.clients.portal.tryrook.io`    — prod portal (cuenta 3 /
 *                                            us-east-1)
 *   - `sandbox.clients.portal.tryrook.io` — sandbox portal (cuenta 8 /
 *                                            us-east-1)
 *   - `development.clients.portal.tryrook.io`     — dev portal (cuenta 8 /
 *                                            us-east-2)
 *
 * Why a single source of truth
 * ----------------------------
 * Every other portal-related URL operation in the codebase MUST
 * resolve through here. Hardcoding origin strings in different files
 * = drift risk = the kind of bug where you remove `clients-tmp` from
 * one place but leave it in another and only discover it months
 * later when an open-redirect attack succeeds because two files
 * disagreed. Defining the list once + exporting a validator forces
 * every callsite to share the same allowlist.
 *
 * Used by
 * -------
 *   - Landing's login handler: validates the `?redirect=` query
 *     param's origin is in the allowlist before issuing the post-
 *     auth redirect (CPF-NFR-028 open-redirect protection)
 *   - Per-env portal's preconnect-hints injector: knows which
 *     origins to render `<link rel="preconnect" href>` for
 *   - SPA's env-switcher component: constructs the destination URL
 *     from the matched origin
 *   - CORS allowOrigins in landing + portal HttpApi configs:
 *     same list, single grep target
 *
 * What this module is NOT
 * -----------------------
 *   - It does NOT decide cookie domain. That's separate
 *     (CPF-NFR-026) and depends on whether we're during cutover
 *     (`.portal.tryrook.io`) or post-cutover (`.clients.portal.
 *     tryrook.io`).
 *   - It does NOT know which origin is "the current" one. That's
 *     `window.location.origin` at the callsite.
 *   - It does NOT do any I/O. Pure data + pure functions; safe to
 *     import from both the SPA and the Lambdas.
 */

/**
 * CUTOVER FLAG — the single switch for the Vercel→Amplify transition.
 *
 * During the cutover, the per-env portals (and the landing) are served
 * under the TRANSITIONAL `clients-tmp` parent, because the FINAL
 * `clients.portal.tryrook.io` is still the legacy Vercel deploy — and
 * its CAA record blocks AWS ACM from issuing a cert for ANY
 * `*.clients.portal.tryrook.io` subdomain (diagnosed 2026-06-01; that's
 * why both the landing and the per-env portals live under `clients-tmp`
 * for now). At cutover (RR-957) the legacy Vercel app is retired,
 * `clients.portal.tryrook.io` becomes the Amplify landing, the CAA
 * unblocks, and the FINAL per-env hosts get their certs.
 *
 * EVERYTHING that picks a "live" origin (the post-login handoff target,
 * the preconnect hints) derives from this one constant. **At cutover,
 * flip this to `false` — that is the only line that changes.**
 */
export const CUTOVER_IN_PROGRESS = true;

/**
 * The 8 trusted portal origins — the open-redirect allowlist that
 * `isTrustedPortalOrigin` checks the `?redirect=` param against
 * (CPF-NFR-028). Contains BOTH the transitional `clients-tmp` hosts AND
 * the final `clients` hosts, so a deep link to EITHER is accepted during
 * and after cutover — they are all ours. After cutover the `-tmp` hosts
 * simply stop resolving in DNS (harmless: the validator still accepts
 * them but nothing serves them; RR-957 can prune them from this list).
 *
 * ORDER MATTERS — keep it stable so a future grep `portal-origins-list`
 * finds entries in this order: landing (tmp, final), then per-env FINAL,
 * then per-env TRANSITIONAL.
 */
export const PORTAL_ORIGINS = [
  // Landing — transitional + final
  "https://clients-tmp.portal.tryrook.io",
  "https://clients.portal.tryrook.io",
  // Per-env portals — FINAL hosts (post-cutover)
  "https://production.clients.portal.tryrook.io",
  "https://sandbox.clients.portal.tryrook.io",
  "https://development.clients.portal.tryrook.io",
  // Per-env portals — TRANSITIONAL `clients-tmp` hosts (live during
  // cutover; the final `*.clients.portal` hosts are CAA-blocked until
  // the Vercel app is retired)
  "https://production.clients-tmp.portal.tryrook.io",
  "https://sandbox.clients-tmp.portal.tryrook.io",
  "https://development.clients-tmp.portal.tryrook.io",
] as const;

export type PortalOrigin = (typeof PORTAL_ORIGINS)[number];

/**
 * Per-env portal origins — FINAL hosts. The post-cutover target set.
 */
export const PER_ENV_PORTAL_ORIGINS_FINAL: readonly PortalOrigin[] = [
  "https://production.clients.portal.tryrook.io",
  "https://sandbox.clients.portal.tryrook.io",
  "https://development.clients.portal.tryrook.io",
];

/**
 * Per-env portal origins — TRANSITIONAL `clients-tmp` hosts. These are
 * the ones that actually resolve + serve TODAY (during cutover).
 */
export const PER_ENV_PORTAL_ORIGINS_TMP: readonly PortalOrigin[] = [
  "https://production.clients-tmp.portal.tryrook.io",
  "https://sandbox.clients-tmp.portal.tryrook.io",
  "https://development.clients-tmp.portal.tryrook.io",
];

/**
 * The LIVE per-env portal origins — the set the post-login handoff
 * targets and the preconnect hints warm. Keyed off `CUTOVER_IN_PROGRESS`
 * so there is exactly ONE switch at cutover: during cutover these are
 * the `-tmp` hosts (which resolve + serve today); post-cutover they
 * become the final hosts. `[0]` is always Production.
 */
export const PER_ENV_PORTAL_ORIGINS: readonly PortalOrigin[] =
  CUTOVER_IN_PROGRESS
    ? PER_ENV_PORTAL_ORIGINS_TMP
    : PER_ENV_PORTAL_ORIGINS_FINAL;

/**
 * The landing origins only (transitional + post-cutover). Used by
 * per-env portal's preconnect-hints injector — the portal needs to
 * preconnect to BOTH landing variants during cutover so logout /
 * expired-session redirect is instant regardless of which landing
 * URL is the live one.
 */
export const LANDING_ORIGINS: readonly PortalOrigin[] = [
  "https://clients-tmp.portal.tryrook.io",
  "https://clients.portal.tryrook.io",
];

/**
 * Check whether a URL's origin is in the trusted allowlist. Returns
 * `true` for any of the 5 portal origins, `false` for everything
 * else INCLUDING malformed URLs, javascript: schemes, or origins
 * that look similar but aren't byte-exact ("clients.portal.tryrook.IO"
 * lowercased to ".io" still rejects because the host comparison is
 * case-sensitive at the URL spec level — modern URL parsers
 * lowercase the host but a custom string comparison would not, so
 * we rely on the URL parser).
 *
 * Strict equality on the parsed `.origin` field is the right check —
 * substring matching ("contains clients.portal.tryrook.io") is a
 * classic open-redirect bypass (an attacker registers
 * `clients.portal.tryrook.io.evil.example.com` and `endsWith` /
 * `includes` accept it). The URL.origin getter normalises the scheme
 * + host + port to a comparable form.
 *
 * Catches:
 *   - Malformed URLs (URL constructor throws → false)
 *   - Schemes other than https: (javascript:, data:, file:, etc.)
 *   - Origins not in the allowlist
 *   - Subdomain attacks like `production.clients.portal.tryrook.io.evil.com`
 *
 * Does NOT catch (intentionally):
 *   - URLs with credentials embedded (`https://user:pass@prod...`) —
 *     URL.origin strips those; the redirect goes to the bare origin
 *   - URLs with weird paths (`...prod.../etc/passwd`) — paths are
 *     allowed because the legitimate redirect uses the path to land
 *     the customer back on their original view
 *   - URLs with weird query strings — same reason as paths
 */
export const isTrustedPortalOrigin = (urlString: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(urlString);
  } catch {
    return false;
  }
  // Reject anything that isn't https — the portal origins are
  // https-only and this protects against javascript: / data: / etc.
  if (parsed.protocol !== "https:") {
    return false;
  }
  return (PORTAL_ORIGINS as readonly string[]).includes(parsed.origin);
};

/**
 * Map a customer-facing `Environment` to its LIVE per-env portal origin.
 *
 * "LIVE" means the host that actually resolves + serves today — derived
 * from `PER_ENV_PORTAL_ORIGINS` which follows `CUTOVER_IN_PROGRESS`. So
 * during cutover this returns the `clients-tmp.portal.tryrook.io` hosts;
 * post-cutover (flip the flag) it returns the `clients.portal.tryrook.io`
 * hosts. Single switch, no per-callsite branching.
 *
 * Used by the per-env portal's `<EnvSwitcher>` (Topbar): the dropdown
 * renders OTHER envs as `<EnvSwitchButton targetOrigin={portalOriginForEnv(opt)}>`
 * so clicking another env is a cross-origin navigation (CPF-NFR-028),
 * never a path-flip on the same host (which would 5xx because the
 * prod-issued bearer token is rejected by the sandbox upstream — see
 * `foundations/AI-PITFALLS.md` P-033).
 *
 * Why an index lookup and not a `Record<Environment, PortalOrigin>` literal:
 * the source of truth is the ordered `PER_ENV_PORTAL_ORIGINS` array
 * (production, sandbox, development). A separate Record would duplicate
 * that data and drift at cutover. The order is pinned by tests in
 * `portalOrigins.test.ts` so a reordering breaks loud.
 */
import type { Environment } from "./appContext";

const ENV_TO_PORTAL_ORIGIN_INDEX: Record<Environment, 0 | 1 | 2> = {
  production: 0,
  sandbox: 1,
  development: 2,
};

export const portalOriginForEnv = (env: Environment): PortalOrigin =>
  PER_ENV_PORTAL_ORIGINS[ENV_TO_PORTAL_ORIGIN_INDEX[env]];

/**
 * Build `<link rel="preconnect" crossorigin>` + `<link rel="dns-prefetch">`
 * hint strings for an array of origins. Used by the SSR /
 * build-time HTML template injectors in the landing + per-env
 * portal packages.
 *
 * Returns an array of HTML link tag strings (no JSX dependency — the
 * function is callable from both client and server contexts).
 * Caller is responsible for joining + inserting into the `<head>`.
 *
 * The `crossorigin` attribute on preconnect is required for the TLS
 * connection to be reusable by authenticated navigations — without
 * it the browser opens a credential-less preconnect that doesn't
 * help when the navigation needs to send a cookie.
 */
export const buildPreconnectHints = (
  origins: readonly PortalOrigin[],
): readonly string[] => {
  const tags: string[] = [];
  for (const origin of origins) {
    tags.push(`<link rel="preconnect" href="${origin}" crossorigin>`);
  }
  for (const origin of origins) {
    tags.push(`<link rel="dns-prefetch" href="${origin}">`);
  }
  return tags;
};
